import { useCallback, useMemo, useRef, type FC } from 'react';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

// 1x1 gif so we don't have img tags without src attributes
const PLACEHOLDER_IMG = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

type SrcSetAttributes = { srcSet?: string; sizes?: string };

export type LazyImageProps = {
    alt: string;
    onImageLoad: () => void;
    srcSetAttributes?: SrcSetAttributes;
    placeholder?: string | null;
    className?: string;
    offsetVertical?: number;
    src?: string;
    imageDraggable?: boolean; // undefined draggable will result in default browser behavior
};

export const LazyImage: FC<LazyImageProps> = ({
    alt,
    onImageLoad,
    srcSetAttributes,
    className,
    offsetVertical = 0,
    src,
    imageDraggable,
}) => {
    const imgRef = useRef<HTMLImageElement | null>(null);

    const visibilityTrackerOptions = useMemo(
        () => ({ rootMargin: `${offsetVertical}px 0px ${offsetVertical}px 0px` }),
        [offsetVertical]
    );

    const triggerImageLoad = useCallback(
        ({ isVisible }: { isVisible: boolean }) => {
            const imageElement = imgRef.current;
            if (!imageElement || !isVisible) {
                return;
            }
            const { srcSet, sizes } = srcSetAttributes || {};
            if (srcSet && sizes) {
                // sizes must be set before srcset (Safari quirk)
                imageElement.sizes = sizes;
                imageElement.srcset = srcSet;
            }
            if (src) {
                imageElement.src = src;
            }
        },
        [srcSetAttributes, src]
    );

    const handleImageLoad = useCallback(() => {
        const imageElement = imgRef.current;
        if (!imageElement || imageElement.src === PLACEHOLDER_IMG) {
            return;
        }
        imageElement.setAttribute('data-tn', 'product-image-lazy-loaded');
        onImageLoad();
    }, [onImageLoad]);

    return (
        <>
            <img
                className={className}
                ref={imgRef}
                src={PLACEHOLDER_IMG}
                alt={alt}
                draggable={imageDraggable}
                onLoad={handleImageLoad}
                data-tn="product-image-not-lazy-loaded"
            />
            <VisibilityTracker
                elementRef={imgRef}
                onVisibilityChange={triggerImageLoad}
                observerOptions={visibilityTrackerOptions}
            />
        </>
    );
};
